// material-ui
import { Avatar, Box, Dialog, DialogContent, Divider, IconButton, Paper, Stack, Tooltip, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// project imports
import LogoSection from "../LogoSection";
// import SearchSection from './SearchSection';
import MobileSection from "./MobileSection";
import ProfileSection from "./ProfileSection";
import SelectSucursal from "./SelectSucursal";

// import LocalizationSection from './LocalizationSection';

import { useEffect, useMemo, useState } from "react";
import io from "socket.io-client";
import { useDispatch, useSelector } from "store";
import { getNotifications, openDrawer } from "store/slices/menu";
import { cambiarTiempoEsperaSucursal, cambiarTiempoTakeAwaySucursal, obtenerTiempoEsperaSucursal, obtenerTiempoTakeAwaySucursal } from "store/slices/sucursal";
import NotificationSection from "./NotificationSection";
import SwitchActividad from "./SwitchActividad";
// assets
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconMenu2 } from "@tabler/icons";
import { TIENDA_HANDY } from "config";
import { useNegocioContext } from "contexts/NegocioContext";
import { getPedidos, refreshPedidosForAutoAccept } from "store/slices/pedidos";
import { getSucursalId } from "utils/axios";
import SelectTiempoSucursal from "./SelectTiempoSucursal/SelectTiempoSucursal";
import SwitchDelivery from "./SwitchDelivery";
import SwitchTakeAway from "./SwitchTakeAway";

// import notification from 'assets/sounds/notification.mp3';

// ==============================|| MAIN NAVBAR / HEADER ||============================== //
const Header = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { negocio } = useNegocioContext();
  const { drawerOpen } = useSelector((state) => state.menu);
  const { pedidos } = useSelector((state) => state.pedidos);
  const { configAvanzada } = useSelector((state) => state.negocio);
  const [dialogOpen, setDialogOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const URL = "https://socket-notificaciones-ev7ccfvftq-uc.a.run.app/";

  const businessId = negocio.negocio_id;
  const isSpecialBusiness = businessId === 542; // temporalmente para negocio especial

  // Evitar múltiples conexiones
  const socket = useMemo(
    () =>
      io.connect(URL, {
        secure: true,
        reconnect: true,
        rejectUnauthorized: false,
        autoConnect: false, // Inicialmente no se conecta automáticamente
      }),
    []
  );

  const notificationAudio = useMemo(() => new Audio("https://storage.googleapis.com/tds-img/public/notification-new.mp3"), []);

  const handleClick = () => {
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const playAudio = (audio) => {
    const audioPromise = audio.play();
    if (audioPromise !== undefined) {
      audioPromise
        .then(() => {})
        .catch((err) => {
          console.info(err);
        });
    }
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (socket) {
      socket.connect();
      socket.on("connect", () => {
        socket.emit("storeClientInfo", { sucursal_id: getSucursalId() });
      });

      socket.on("connect_error", (err) => {
        console.error("Error de conexión:", err);
      });

      socket.on("enviarNotificacion", (data) => {
        playAudio(notificationAudio);
        dispatch(getNotifications()).finally(() => {
          // Verificar si hay aceptado automático activo para ajustar el comportamiento
          if (configAvanzada?.automatic_accept_orders) {
            // Para aceptado automático: múltiples actualizaciones más frecuentes
            // porque los pedidos entran directamente como "PREPARANDO"
            dispatch(refreshPedidosForAutoAccept()); // Inmediata

            setTimeout(() => {
              dispatch(refreshPedidosForAutoAccept());
            }, 1000);

            setTimeout(() => {
              dispatch(refreshPedidosForAutoAccept());
            }, 2500);

            setTimeout(() => {
              dispatch(refreshPedidosForAutoAccept());
            }, 4000);
          } else {
            // Flujo normal para pedidos manuales
            setTimeout(() => {
              dispatch(getPedidos());
            }, 1500);
          }
        });
      });

      return () => {
        socket.off("connect");
        socket.off("connect_error");
        socket.off("enviarNotificacion");
        socket.disconnect();
      };
    }
  }, [dispatch, socket, notificationAudio, configAvanzada?.automatic_accept_orders]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      pedidos.forEach((pedido) => {
        if (pedido.estado === "INGRESADO") {
          playAudio(notificationAudio);
        }
      });
    }, 10000);

    return () => clearInterval(intervalId);
  }, [pedidos, notificationAudio]);

  return (
    <Paper
      elevation={1}
      sx={{
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar,
        backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 1.5, sm: 2 },
          py: 1,
          minHeight: 56,
        }}>
        {/* Left Section - Logo and Menu Toggle */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: theme.palette.primary.contrastText,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: drawerOpen ? "rotate(180deg)" : "rotate(0deg)",
              boxShadow: theme.palette.mode === "dark" ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.1)",
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.main})`,
                transform: drawerOpen ? "rotate(180deg) scale(1.05)" : "rotate(0deg) scale(1.05)",
                boxShadow: theme.palette.mode === "dark" ? "0 6px 20px rgba(0,0,0,0.4)" : "0 6px 20px rgba(0,0,0,0.15)",
              },
            }}
            onClick={() => dispatch(openDrawer(!drawerOpen))}>
            <IconMenu2 stroke={1.5} size="1.2rem" />
          </Avatar>

          <Box
            component="span"
            sx={{
              display: { xs: "none", md: "block" },
              ml: TIENDA_HANDY ? 0 : 0.5,
            }}>
            <LogoSection />
          </Box>
        </Stack>

        {/* Center Section - Time Controls (Desktop Only) */}
        {!isMobile && (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1, justifyContent: "center", mx: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1,
                borderRadius: 1.5,
                backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[50],
                border: `1px solid ${theme.palette.divider}`,
              }}>
              {isSpecialBusiness ? (
                <Box sx={{ px: 2, py: 1 }}>
                  <span>Tiempo de demora: 24 horas</span>
                </Box>
              ) : (
                <>
                  <SelectTiempoSucursal
                    label="Tiempo de demora"
                    selector={(state) => state.sucursal.tiempoEsperaSucursal}
                    fetchAction={obtenerTiempoEsperaSucursal}
                    updateAction={cambiarTiempoEsperaSucursal}
                  />
                  <Divider orientation="vertical" flexItem />
                  <SelectTiempoSucursal
                    label="Tiempo takeaway"
                    selector={(state) => state.sucursal.tiempoTakeAwaySucursal}
                    fetchAction={obtenerTiempoTakeAwaySucursal}
                    updateAction={cambiarTiempoTakeAwaySucursal}
                  />
                </>
              )}
            </Box>
          </Stack>
        )}

        {/* Right Section - Controls and Profile */}
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <NotificationSection />
          <SelectSucursal />

          {/* Store Status Menu */}
          <Tooltip title="Estados de la Tienda" placement="bottom">
            <IconButton
              onClick={handleClick}
              size="small"
              sx={{
                background: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  background: theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[200],
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease-in-out",
              }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Dialog
            open={dialogOpen}
            onClose={handleClose}
            maxWidth={false}
            PaperProps={{
              sx: {
                backgroundColor: "transparent",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                boxShadow: theme.palette.mode === "dark" ? "0 8px 32px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.08)",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
                    : "linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)",
                minWidth: 300,
                p: 0,
              },
            }}>
            <DialogContent
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                position: "relative",
              }}>
              {/* Close Button */}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  width: 32,
                  height: 32,
                  background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                  "&:hover": {
                    background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}>
                <CloseIcon fontSize="small" />
              </IconButton>

              <Box
                sx={{
                  py: 1.5,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  borderRadius: 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                    transform: "translateY(-1px)",
                  },
                }}>
                <SwitchActividad />
              </Box>
              <Divider
                sx={{
                  width: "90%",
                  borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                }}
              />
              <Box
                sx={{
                  py: 1.5,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  borderRadius: 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                    transform: "translateY(-1px)",
                  },
                }}>
                <SwitchDelivery />
              </Box>
              <Divider
                sx={{
                  width: "90%",
                  borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                }}
              />
              <Box
                sx={{
                  py: 1.5,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  borderRadius: 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                    transform: "translateY(-1px)",
                  },
                }}>
                <SwitchTakeAway />
              </Box>
            </DialogContent>
          </Dialog>

          <ProfileSection />

          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <MobileSection />
          </Box>
        </Stack>
      </Box>

      {/* Mobile Time Controls */}
      {isMobile && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            {isSpecialBusiness ? (
              <Box sx={{ px: 2, py: 1, textAlign: "center" }}>
                <span>Tiempo de demora: 24 horas</span>
              </Box>
            ) : (
              <>
                <SelectTiempoSucursal label="Demora" selector={(state) => state.sucursal.tiempoEsperaSucursal} fetchAction={obtenerTiempoEsperaSucursal} updateAction={cambiarTiempoEsperaSucursal} />
                <SelectTiempoSucursal
                  label="Takeaway"
                  selector={(state) => state.sucursal.tiempoTakeAwaySucursal}
                  fetchAction={obtenerTiempoTakeAwaySucursal}
                  updateAction={cambiarTiempoTakeAwaySucursal}
                />
              </>
            )}
          </Stack>
        </Box>
      )}

      {/* Hidden audio element for notifications */}
      <audio id="notification" src="assets/sounds/notification" muted="muted" style={{ display: "none" }} />
    </Paper>
  );
};

export default Header;
